import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import { App } from './App'
import { armPushTaps } from './push/pushOptIn'
import { openPushPath } from './push/pushNav'
import { Authed } from './auth/Authed'
import { ErrorBoundary } from './shell/ErrorBoundary'
import {
  isChatv1Callback,
  isChatv1CallbackPath,
  handleChatv1Callback,
  consumeChatv1Return,
  ensureChatv1Token,
} from './chatv1/auth'
import { bootstrapChatv1 } from './chatv1/bootstrap'
import { LegalPage } from './legal/LegalPage'
import { legalDocForPath } from './legal/legalRoute'
import type { Doc } from './legal/documents'
import { artifactsHaveMoved } from './artifacts/contentHost'
import { retireArtifactWorkers } from './artifacts/retireWorkers'
import { captureGroupLink } from './groups/groupLink'
import { captureInviteLink } from './friends/inviteLink'

// Before the first render: the shell sends `?shell=<origin>` once, at pairing, and
// Disconnect needs it much later — after relaunches that carry no parameters at all.

// A group's link (`?group=<code>`, groups/groupLink.ts): kept before anything renders, because the person
// may sign in first and that round trip lands on "/" with nothing of ours.
captureGroupLink()
// A person's invite link (`?invite=<code>`, friends/inviteLink.ts) — kept the same way, for the same reason.
captureInviteLink()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The client can never trust that its stream is still subscribed
      // (docs/ARCHITECTURE.md §6.2), so refetching on focus is part of the
      // reconciliation posture, not a convenience.
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

function renderApp(): void {
  const root = document.getElementById('root')
  if (!root) throw new Error('#root missing')
  /*
   * A NOTIFICATION TAP IS DELIVERED AT LAUNCH, so the listener is armed here and
   * not when some panel mounts. On a cold start iOS hands the tap over
   * immediately after the app starts; a listener attached a few renders later has
   * already missed the only event it exists for, and the person lands on whatever
   * screen they left rather than the agent they tapped.
   *
   * Fire-and-forget, and silent in a build without the plugin: this must never be
   * the thing that stops the app from rendering.
   */
  void armPushTaps(openPushPath)
  createRoot(root).render(
    <StrictMode>
      {/* Outside the provider on purpose: a throw from the query client's own render
          path has to be caught too, and inside it that one would still blank. */}
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Authed>
            {/*
              * `App` — the product, not a picker in front of it. The transport for its
              * screens is `src/transport/relay.ts` (commands down the machine's link); with
              * the Project-Chat-on-DynamoDB flag on, the project screens speak to the chat
              * service instead, gated in api.ts. The rest of the app is unchanged.
              */}
            <App />
          </Authed>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  )
}

/**
 * Boot. The chat-v1 (DynamoDB) login is wired HERE, ahead of render, and ONLY when the
 * Project-Chat-on-DynamoDB flag is on — with the flag off (the default) none of this runs
 * and the app starts exactly as before.
 *
 *  1. `?chat=dynamo|edge` seeds the flag so a phone can flip the cutover without devtools.
 *  2. A `/chat/callback?code=` landing is completed before anything renders, then the code
 *     is stripped from the URL and we render on the restored path with a token in hand.
 *  3. With the flag on and no token obtainable silently, start the Cognito login — a silent
 *     bounce when the Google session is already present. A sessionStorage guard means a
 *     login that cannot complete degrades to "render without a chat token" (the rest of the
 *     app works; Project Chat shows its own error) instead of a redirect loop.
 *  4. Render, then fire the silent bootstrap (device register + socket) for a returning user.
 */
/**
 * The public legal pages — `/privacy` and `/terms` — render OUTSIDE the app: no
 * session, no chat token, no providers, no sign-in gate. App Store Connect links to
 * one of them and a reviewer opens it signed out; it must read as a document, not
 * as a sign-in screen. Same words as the in-app sheet (documents.ts, one source).
 */
function renderLegal(doc: Doc): void {
  const root = document.getElementById('root')
  if (!root) throw new Error('#root missing')
  // A DOCUMENT, not the app: the shell's body is position:fixed / overflow:hidden so
  // the on-screen keyboard can drive --app-h. A legal page has no keyboard and no
  // panes — it is read, scrolled and copied from like any page. The class hands the
  // body back to the browser (shell.css `html.legal-doc`). Owner, 2026-09-12: /privacy
  // 以及 /terms 都無法向下捲動 — measured on prod: 5597px of policy in an 844px box.
  document.documentElement.classList.add('legal-doc')
  createRoot(root).render(
    <StrictMode>
      <LegalPage doc={doc} />
    </StrictMode>,
  )
}

async function boot(): Promise<void> {
  const legal = legalDocForPath(window.location.pathname)
  if (legal) {
    renderLegal(legal)
    return
  }
  if (isChatv1CallbackPath()) {
    // Back from Cognito — a `?code` success OR a `?error`/empty failure both land here.
    // Exchange the code when present, then ALWAYS restore the URL and drop the one-shot
    // login guard. Clearing it on the error path too is what stops a failed federation
    // from wedging the tab tokenless forever (only a tab-close cleared it before).
    if (isChatv1Callback()) {
      try {
        await handleChatv1Callback()
      } catch {
        /* fall through to render; the Projects panel offers a Sign in button if tokenless */
      }
    }
    const back = consumeChatv1Return()
    window.history.replaceState(null, '', back)
    sessionStorage.removeItem('av_chat_login_tried')
  } else {
    // Mint the chat token from the app's own session (same-origin /chat/token, via
    // refreshChatv1Token) so it's in hand before the project screens read anything —
    // no OAuth redirect, so this works inside an iOS home-screen PWA. A failure here
    // just renders degraded; the transport re-mints on demand.
    try {
      await ensureChatv1Token()
    } catch {
      /* render anyway; the transport mints again on first use */
    }
  }

  renderApp()
  // Returning user with a stored refresh token: connect the socket + register the device
  // silently. A first-time user has already been sent through the redirect above.
  void bootstrapChatv1()
  /*
   * Service workers that artifact pages left on THIS origin, once those pages have moved
   * to a host of their own (owner, 2026-10-05) — retireWorkers.ts says why only script
   * here can remove them. After the render and never awaited: it asks the cloud nothing
   * unless such a worker exists, and it must never be what holds the app back.
   */
  void retireArtifactWorkers(() => artifactsHaveMoved(queryClient))
}

void boot()
